import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { moduleKeys } from '@entities/Module';
import { trackEvent } from '@shared/analytics';
import { toast } from '@shared/ui';
import { clearDemoWords, readDemoWords } from './demoWords';
import { saveStoryWords } from './storyWords';

/**
 * Слова из итогов демо ждут аккаунта: как только `enabled` (ученик вошёл) — сохраняем их в модуль «Выживи в Лондоне»,
 * говорим тостом и отдаём модуль в `onSaved` (страница откроет его — «чтобы сразу можно было заучивать», владелец
 * 2026-10-25). С 2026-10-30 — сразу после регистрации, без подтверждения почты (владелец: «сохраняй слова»; итоги демо
 * обещают «тренируй сразу после регистрации»). Работает на /demo/words и на главной. Сбой — слова остаются ждать
 * (до суток), повтор при следующем заходе; `toastOnFail` — сказать об этом (на главной иначе сбой не виден).
 */
export function useClaimDemoWords(
  enabled: boolean,
  onSaved: (moduleId: string) => void,
  toastOnFail = false,
): 'idle' | 'saving' | 'failed' {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [state, setState] = useState<'idle' | 'saving' | 'failed'>('idle');
  const busy = useRef(false);
  const saved = useRef(onSaved);
  saved.current = onSaved;

  useEffect(() => {
    if (!enabled || busy.current) return;
    const words = readDemoWords();
    if (!words) return;
    busy.current = true;
    setState('saving');
    const name = t('stories.bridge.moduleName');
    saveStoryWords(words, name)
      .then(({ moduleId }) => {
        clearDemoWords();
        void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
        trackEvent('demo_words_saved', { count: words.length });
        toast({ tone: 'success', message: t('stories.demo.wordsSaved', { name }) });
        setState('idle');
        saved.current(moduleId);
      })
      .catch(() => {
        busy.current = false;
        setState('failed');
        if (toastOnFail) toast({ tone: 'error', message: t('stories.demo.wordsRetry') });
      });
  }, [enabled, qc, t, toastOnFail]);

  return state;
}
